import React, {ReactNode, useEffect, useRef, useState} from 'react';
import {v4 as uuid} from 'uuid';
import {
  Body,
  Button,
  Checkbox,
  designSystemToken,
  Input,
} from '@lightricks/react-design-system';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import Fortress from '@/services/fortress/Fortress';
import {
  buttonNames,
  flowNames,
  reasons,
  screenNames,
  providers,
} from '@/lib/delta/deltaConstants.js';
import translate from '@/utils/translate';
import emailValidator from '@/utils/validators/email';
import auth from '@/api/auth';
import {
  FormValidatorsMapProps,
  InputFieldsMap,
  AuthFormProps,
  ValidatorEntry,
  ValidatorValue,
} from '@/components/auth-form/AuthFormProps';
import useTrackAuthStepEvent from '@/hooks/analytics/use-track-auth-step-event';
import useTrackButtonEvent from '@/hooks/analytics/use-track-button-event';
import styles from './AuthForm.module.scss';

const formValidatorsMap: ({
  requireTermsAgreement,
}: {
  requireTermsAgreement: boolean;
}) => FormValidatorsMapProps = ({requireTermsAgreement}) => ({
  firstName: [
    {
      validator: (firstName: string) => firstName !== '',
      errorMessage: translate('components.auth.auth-form.errors.first-name'),
    },
  ],
  lastName: [
    {
      validator: (lastName: string) => lastName !== '',
      errorMessage: translate('components.auth.auth-form.errors.last-name'),
    },
  ],
  brandName: [
    {
      validator: (brandName: string) => brandName !== '',
      errorMessage: translate('components.auth.auth-form.errors.brand-name'),
    },
  ],
  email: [
    {
      validator: (email: string) => email !== '',
      errorMessage: translate('components.auth.auth-form.errors.email'),
    },
    {
      validator: emailValidator,
      errorMessage: translate('components.auth.auth-form.errors.email-invalid'),
    },
  ],
  isTermsChecked: [
    {
      validator: (isTermsChecked: boolean) =>
        requireTermsAgreement ? isTermsChecked : true,
      errorMessage: translate('components.auth.auth-form.errors.terms'),
    },
  ],
  isTestBrandChecked: [
    {
      validator: (isTestBrandChecked: boolean) => true,
      errorMessage: '123',
    },
  ],
});

function AuthForm(props: AuthFormProps) {
  const {
    form,
    setForm,
    isLoading,
    setIsLoading,
    setGeneralError,
    setIsModalOpen,
    isMobile,
    disableEmail = false,
    emailLabelKey = 'components.auth.auth-form.email-label',
    testID = 'auth-form',
    submitButtonAppearance = 'brand',
    onlyEmail = false,
    requireTermsAgreement = true,
    submitButtonTextKey = 'Sign up with email',
    mode = 'signup',
    fortressFlowName,
    captchaToken,
    refreshCaptchaToken,
  } = props;
  const [errors, setErrors] = useState({
    email: '',
    firstName: '',
    lastName: '',
    brandName: '',
    isTermsChecked: '',
    isTestBrandChecked: '',
  });
  const [internalEmail, setInternalEmail] = useState(false);
  const [emailExistsError, setEmailExistsError] = useState<ReactNode>('');
  const [brandNameExistsError, setBrandNameExistsError] =
    useState<ReactNode>('');
  const firstInputRef = useRef<HTMLInputElement | null>(null);
  const numberOfFields = 5;
  const formValidators = formValidatorsMap({requireTermsAgreement});
  const {pressed: trackButtonPressed} = useTrackButtonEvent();
  const {started: trackAuthStepStarted, ended: trackAuthStepEnded} =
    useTrackAuthStepEvent(mode);

  useEffect(() => {
    if (firstInputRef.current && !isMobile) {
      firstInputRef.current?.focus();
    }
  }, []);

  useEffect(() => {
    if (
      form.email.endsWith('@lightricks.com') ||
      form.email.endsWith('@popularpays.com')
    ) {
      setInternalEmail(true);
      setFormValue('isTestBrandChecked', true);
    } else {
      setInternalEmail(false);
      setFormValue('isTestBrandChecked', false);
    }
  }, [form.email]);

  const inputFields: InputFieldsMap = {
    firstName: () => ({
      label: translate('components.auth.auth-form.firstname-label'),
      value: form.firstName || '',
      validatorOptions: {
        onChangeText: (value: string) => setFormValue('firstName', value),
        validators: formValidators.firstName,
        overrideError: errors.firstName,
      },
      inputRef: firstInputRef,
      testID: `${testID}--firstname-input`,
    }),
    lastName: () => ({
      label: translate('components.auth.auth-form.lastname-label'),
      value: form.lastName || '',
      validatorOptions: {
        onChangeText: (value: string) => setFormValue('lastName', value),
        validators: formValidators.lastName,
        overrideError: errors.lastName,
      },
      testID: `${testID}--lastname-input`,
    }),
    brandName: () => ({
      label: translate('components.auth.auth-form.brand-name-label'),
      value: form.brandName || '',
      validatorOptions: {
        onChangeText: (value: string) => setFormValue('brandName', value),
        validators: formValidators.brandName,
        overrideError: brandNameExistsError || errors.brandName,
      },
      testID: `${testID}--brand-name-input`,
    }),
    email: () => ({
      label: translate(emailLabelKey),
      value: form.email,
      validatorOptions: {
        onChangeText: (value: string) => setFormValue('email', value),
        validators: formValidators.email,
        overrideError: emailExistsError || errors.email,
      },
      testID: `${testID}--email-input`,
    }),
  };

  const setFormValue = (key: string, value: string | boolean) => {
    setForm({
      ...form,
      [key]: value,
    });
    setErrors({...errors, [key]: ''});
    setEmailExistsError(null);
  };

  const validateField = (
    value: ValidatorValue,
    validators: ValidatorEntry<ValidatorValue>[]
  ) => {
    let fieldErrors = '';

    validators.forEach((validator) => {
      if (!fieldErrors.length && !validator.validator(value)) {
        fieldErrors = validator.errorMessage;
      }
    });

    return fieldErrors.length ? fieldErrors : undefined;
  };

  const validateFormWithData = (formData: typeof form) => {
    const formErrors: Record<string, string> = {};

    Object.entries(formValidators).forEach(([fieldName, validators]) => {
      const value = formData[fieldName as keyof typeof formData];
      if (value !== undefined) {
        const fieldErrors = validateField(value, validators);
        if (fieldErrors) {
          formErrors[fieldName] = fieldErrors;
        }
      }
    });

    return {formErrors};
  };

  const updateFormForOnlyEmail = (currentForm: typeof form) => {
    let updatedForm = {...currentForm};

    if (onlyEmail && currentForm.email) {
      const emailPrefix = currentForm.email.split('@')[0];
      updatedForm = {
        ...updatedForm,
        brandName: currentForm.email,
        firstName: emailPrefix,
        lastName: emailPrefix,
      };
      setForm(updatedForm);
    }

    return updatedForm;
  };

  const refreshCaptchaTokenIfNeeded = async () => {
    if (!refreshCaptchaToken) {
      return null;
    }
    // Always refresh to ensure token is valid and not about to expire
    return refreshCaptchaToken(true);
  };

  const onSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setGeneralError({title: '', message: ''});

    const updatedForm = updateFormForOnlyEmail(form);

    const signupFlowEvent = AnalyticsService.getFlow(flowNames.AUTH.NEW_SIGNUP);
    trackButtonPressed({
      button_name: buttonNames.SIGNUP.CREATE_YOUR_ACCOUNT,
      screen_name: screenNames.AUTH.NEW_SIGNUP,
      flow_id: signupFlowEvent?.flow_id || '',
      flow_name: signupFlowEvent?.flow_name || '',
      triggered_flow_id: '',
      triggered_flow_name: '',
      screen_presentation_id: '',
      campaign_id: '',
      creator_id: '',
      tab: '',
    });

    const processId = uuid();
    const stepEventData = {
      flow_id: signupFlowEvent?.flow_id || '',
      process_id: processId,
      provider: providers.FORTRESS,
      request_type: 'send_otp',
      input_value: [
        updatedForm.email,
        updatedForm.brandName,
        updatedForm.firstName,
        updatedForm.lastName,
      ].join(', '),
    };

    const {formErrors} = validateFormWithData(updatedForm);
    const numberOfFieldsCompleted =
      numberOfFields - Object.values(formErrors).length;

    trackAuthStepStarted({
      ...stepEventData,
      number_of_required_fields: numberOfFields,
      number_of_fields_completed: numberOfFieldsCompleted,
    });

    if (Object.keys(formErrors).length) {
      setErrors({...errors, ...formErrors});
      setIsLoading(false);
      trackAuthStepEnded({
        ...stepEventData,
        reason: reasons.AUTH.FAILURE,
        error: Object.values(formErrors).join(', '),
      });
      return;
    }
    try {
      const {email, brandName} = updatedForm;
      const {brand_name_exists, email_address_exists} =
        await auth.checkBrandNameOrEmailExists(email, brandName || '');
      if (email_address_exists || brand_name_exists) {
        const emailError = email_address_exists
          ? buildFieldExistsError(
              translate(
                'components.auth.auth-form.errors.email-exist.first-plain-text'
              ),
              translate(
                'components.auth.auth-form.errors.email-exist.link-url'
              ),
              translate(
                'components.auth.auth-form.errors.email-exist.link-text'
              ),
              translate(
                'components.auth.auth-form.errors.email-exist.second-plain-text'
              )
            )
          : '';
        const emailErrorForEvents = email_address_exists
          ? translate('components.auth.auth-form.errors.email-exist-for-events')
          : '';
        const brandNameError = brand_name_exists
          ? buildFieldExistsError(
              translate(
                'components.auth.auth-form.errors.brand-name-exist.first-plain-text'
              ),
              translate(
                'components.auth.auth-form.errors.brand-name-exist.link-url'
              ),
              translate(
                'components.auth.auth-form.errors.brand-name-exist.link-text'
              ),
              translate(
                'components.auth.auth-form.errors.brand-name-exist.second-plain-text'
              )
            )
          : '';
        const brandNameErrorForEvents = brand_name_exists
          ? translate(
              'components.auth.auth-form.errors.brand-name-exist-for-events'
            )
          : '';

        setEmailExistsError(emailError);
        setBrandNameExistsError(brandNameError);
        trackAuthStepEnded({
          ...stepEventData,
          reason: reasons.AUTH.FAILURE,
          error: `${emailErrorForEvents}${
            emailErrorForEvents ? ',' : ''
          } ${brandNameErrorForEvents}`,
        });
        setIsLoading(false);
        return;
      }

      const refreshedToken = await refreshCaptchaTokenIfNeeded();
      const effectiveCaptchaToken = refreshedToken || captchaToken;
      if (!effectiveCaptchaToken) {
        trackAuthStepEnded({
          ...stepEventData,
          reason: reasons.AUTH.FAILURE,
          error: 'missing captchaToken',
        });
        return;
      }
      await Fortress.generateOTP(
        updatedForm.email,
        fortressFlowName,
        effectiveCaptchaToken
      );
      setIsModalOpen(true);
      setIsLoading(false);

      trackAuthStepEnded({
        ...stepEventData,
        reason: reasons.AUTH.SUCCESS,
        error: null,
      });

      AnalyticsService.screenDismissed(
        screenNames.AUTH.NEW_SIGNUP,
        screenNames.AUTH.NEW_SIGNUP_OTP
      );
      AnalyticsService.screenPresented(
        screenNames.AUTH.NEW_SIGNUP_OTP,
        screenNames.AUTH.NEW_SIGNUP
      );
    } catch (err: any) {
      const error =
        err?.message ||
        err?.errors?.map((errr: any) => errr?.detail || '').join(', ') ||
        translate('views.auth.signup.general-error-message');
      setGeneralError({
        title: translate('views.auth.signup.general-error'),
        message: error,
      });
      trackAuthStepEnded({
        ...stepEventData,
        reason: reasons.AUTH.FAILURE,
        error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setGeneralError({title: '', message: ''});

    const updatedForm = updateFormForOnlyEmail(form);

    const loginFlowEvent = AnalyticsService.getFlow(flowNames.AUTH.LOGIN);
    trackButtonPressed({
      button_name: buttonNames.LOGIN.LOGIN,
      screen_name: screenNames.AUTH.LOGIN,
      flow_id: loginFlowEvent?.flow_id || '',
      flow_name: loginFlowEvent?.flow_name || '',
      triggered_flow_id: '',
      triggered_flow_name: '',
      screen_presentation_id: '',
      campaign_id: '',
      creator_id: '',
      tab: '',
    });

    const processId = uuid();
    const stepEventData = {
      flow_id: loginFlowEvent?.flow_id || '',
      process_id: processId,
      provider: providers.FORTRESS,
      request_type: 'send_otp',
      input_value: form.email,
    };

    trackAuthStepStarted({
      ...stepEventData,
    });

    const {formErrors} = validateFormWithData(updatedForm);

    if (Object.keys(formErrors).length) {
      setErrors({...errors, ...formErrors});
      setIsLoading(false);
      trackAuthStepEnded({
        ...stepEventData,
        reason: reasons.AUTH.FAILURE,
        error: Object.values(formErrors).join(', '),
      });
      return;
    }

    try {
      const emailExists = await auth.checkEmailExists(form.email);
      if (!emailExists) {
        const emailError = translate(
          'components.auth.auth-form.errors.email-not-exist'
        );

        setEmailExistsError(emailError);
        trackAuthStepEnded({
          ...stepEventData,
          reason: reasons.AUTH.FAILURE,
          error: emailError,
        });
        setIsLoading(false);
        return;
      }

      const refreshedToken = await refreshCaptchaTokenIfNeeded();
      const effectiveCaptchaToken = refreshedToken || captchaToken;
      if (!effectiveCaptchaToken) {
        trackAuthStepEnded({
          ...stepEventData,
          reason: reasons.AUTH.FAILURE,
          error: 'missing captchaToken',
        });
        return;
      }
      await Fortress.generateOTP(
        form.email,
        fortressFlowName,
        effectiveCaptchaToken
      );
      setIsModalOpen(true);
      setIsLoading(false);

      trackAuthStepEnded({
        ...stepEventData,
        reason: reasons.AUTH.SUCCESS,
        error: null,
      });

      AnalyticsService.screenDismissed(
        screenNames.AUTH.LOGIN,
        screenNames.AUTH.LOGIN_OTP
      );
      AnalyticsService.screenPresented(
        screenNames.AUTH.LOGIN_OTP,
        screenNames.AUTH.LOGIN
      );
    } catch (err: any) {
      const error =
        err?.message ||
        err?.errors?.map((errr: any) => errr?.detail || '').join(', ') ||
        translate('views.auth.login.general-error-message');
      setGeneralError({
        title: translate('views.auth.login.general-error'),
        message: error,
      });
      trackAuthStepEnded({
        ...stepEventData,
        reason: reasons.AUTH.FAILURE,
        error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const buildFieldExistsError = (
    firstPlainText: string,
    linkUrl: string,
    linkText: string,
    secondPlainText: string
  ) => {
    return (
      <span className={styles.linkFieldError}>
        {firstPlainText}
        <a href={linkUrl} target="_blank" rel="noreferrer">
          {linkText}
        </a>
        {secondPlainText}
      </span>
    );
  };

  const renderTermsAndPrivacy = () => {
    return (
      <Body
        size="md"
        color={designSystemToken('semantic.fg.secondary')}
        dangerouslyInnerHTML={translate(
          'components.auth.auth-form.terms-label'
        )}
      />
    );
  };

  const renderTestBrandText = () => {
    return (
      <Body
        size="md"
        color={designSystemToken('semantic.fg.secondary')}
        dangerouslyInnerHTML={translate(
          'components.auth.auth-form.test-brand-label'
        )}
      />
    );
  };

  const renderFormInput = (inputNameKey: string, disabled = false) => {
    const {
      label,
      value,
      validatorOptions,
      testID: inputTestID,
      inputRef,
    } = inputFields[inputNameKey as keyof InputFieldsMap]();
    return (
      <Input
        label={label}
        value={value}
        validatorOptions={validatorOptions}
        testID={inputTestID}
        inputRef={inputRef}
        disabled={disabled}
      />
    );
  };

  return (
    <form
      className={styles.container}
      onSubmit={mode === 'signup' ? onSignupSubmit : onLoginSubmit}
      data-testid={testID}
    >
      {!onlyEmail && (
        <div className={styles.inputGroup}>
          <div>{renderFormInput('firstName')}</div>
          <div>{renderFormInput('lastName')}</div>
        </div>
      )}
      {!onlyEmail && <div>{renderFormInput('brandName')}</div>}
      <div>{renderFormInput('email', disableEmail)}</div>
      {internalEmail && mode === 'signup' && (
        <div className={styles.testBrandCheckbox}>
          <Checkbox
            label={renderTestBrandText()}
            checked={form.isTestBrandChecked}
            onChange={(value: boolean) => {
              setFormValue('isTestBrandChecked', value);
            }}
            error={errors.isTestBrandChecked}
          />
        </div>
      )}
      {requireTermsAgreement && (
        <div className={styles.authCheckbox}>
          <Checkbox
            label={renderTermsAndPrivacy()}
            checked={form.isTermsChecked}
            onChange={(value: boolean) => {
              setFormValue('isTermsChecked', value);
            }}
            error={errors.isTermsChecked}
          />
        </div>
      )}
      <Button
        className={styles.submitButton}
        testID={`${testID}--submit-button`}
        appearance={submitButtonAppearance}
        mode="filled"
        size="large"
        type="submit"
        isLoading={isLoading}
      >
        {translate(submitButtonTextKey)}
      </Button>
    </form>
  );
}

export default AuthForm;
